import ExcelJS from "exceljs";
import ColorLogo from "../assets/login/ColorLogo.png";
import logo1 from "../assets/header/HABD.png";
import logo2 from "../assets/login/DMW_Patiala.png";

// Helper functions for formatting (same as in LogSearch component)
const formatSourceScript = (script) => {
  if (!script) return '';
  
  // Remove .js extension
  let formatted = script.replace(/\.js$/, '');
  
  // Special cases
  if (formatted === 'HabdStatus') return 'HABD Status';
  if (formatted === 'TemperatureStatus') return 'Temperature Status';
  if (formatted === 'ProxyStatus') return 'Proxy Status';
  if (formatted === 'PlcStatus') return 'PLC Status';
  if (formatted === 'InternetConnectivity') return 'Internet Connectivity';
  if (formatted === 'RestartStatus') return 'Restart Status';
  
  // Default: insert space before capital letters and capitalize first letter
  formatted = formatted.replace(/([A-Z])/g, ' $1')
                       .replace(/^./, str => str.toUpperCase())
                       .trim();
  
  return formatted;
};

const formatCategory = (category) => {
  if (!category) return 'N/A';
  
  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();
  
  // Format time as "12:38 PM"
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format
  
  return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
};

const formatAdditionalData = (data) => {
  if (!data) return 'N/A';
  try {
    const parsed = JSON.parse(data);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return data;
  }
};

const DownloadSystemLogsExcel = async (
  selectedSite,
  selectedSiteName,
  systemLogs,
  timePeriod,
  setIsLoading
) => {
  try {
    setIsLoading(true);
    
    const user = JSON.parse(localStorage.getItem("user"));
    const userName = user?.username || "Guest";
    const downloadDate = new Date();
    const formattedDateTime = formatDate(downloadDate); // Using the same format as table
    
    // Calculate date range for time period
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - parseInt(timePeriod));
    
    const formattedFromDate = formatDate(fromDate);
    const formattedToDate = formatDate(toDate);
    
    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("System Logs");
    
    try {
      // Fetch images
      const colorLogoBuffer = await fetch(ColorLogo).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch ColorLogo.png");
        return res.arrayBuffer();
      });
      const logo1Buffer = await fetch(logo1).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch HABD.png");
        return res.arrayBuffer();
      });
      const logo2Buffer = await fetch(logo2).then((res) => {
        if (!res.ok) throw new Error("Failed to fetch DMW_Patiala.png");
        return res.arrayBuffer();
      });
      
      // Add images to workbook
      const colorLogoId = workbook.addImage({
        buffer: colorLogoBuffer,
        extension: "png",
      });
      const logo1Id = workbook.addImage({
        buffer: logo1Buffer,
        extension: "png",
      });
      const logo2Id = workbook.addImage({
        buffer: logo2Buffer,
        extension: "png",
      });
      
      // Merge cells for logos and title (adjusted for 7-column table)
      worksheet.mergeCells("A1:B1"); // ColorLogo area
      worksheet.mergeCells("C1:F1"); // Logo1 + Title area (extended by one column)
      worksheet.mergeCells("G1"); // Logo2 area (shifted right by one column)
      
      // Add ColorLogo centered in A1:B1
      worksheet.addImage(colorLogoId, {
        tl: { col: 0.5, row: 0.5 },
        ext: { width: 120, height: 60 },
      });
      worksheet.getCell("A1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      
      // Add Logo1 with slight offset in C1:F1 to allow gap before title
      worksheet.addImage(logo1Id, {
        tl: { col: 2.2, row: 0.5 }, // Slight left offset inside merged range
        ext: { width: 60, height: 60 },
      });
      
      // Add title with proper spacing after logo1
      const title = "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      worksheet.getCell("C1").value = title;
      worksheet.getCell("C1").font = {
        bold: true,
        size: 14,
        name: "Helvetica",
      };
      worksheet.getCell("C1").alignment = {
        horizontal: "left", // Left align so it appears after logo
        vertical: "middle",
        indent: 8, // Adds spacing between logo and text
      };
      
      // Add Logo2 centered in G1 (shifted right by one column)
      worksheet.addImage(logo2Id, {
        tl: { col: 6.5, row: 0.5 }, // Shifted right by one column
        ext: { width: 80, height: 60 },
      });
      worksheet.getCell("G1").alignment = {
        horizontal: "center",
        vertical: "middle",
      };
      
      // Set height for header row
      worksheet.getRow(1).height = 65;
      // console.log("All logos added successfully to header");
    } catch (error) {
      console.error("Error loading logo images:", error);
      
      // Fallback layout without logos
      worksheet.mergeCells("A1:B1");
      worksheet.mergeCells("C1:F1"); // Extended by one column
      worksheet.mergeCells("G1"); // Shifted right by one column
      worksheet.getCell("A1").value = "NOVIUS";
      worksheet.getCell("A1").font = { bold: true, size: 14, color: { argb: "FF0066CC" } };
      worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
      worksheet.getCell("C1").value = "NOVIUS HABD (Lite) - NOVIUS HOT AXLE BOX DETECTION SYSTEM (LITE MODEL)";
      worksheet.getCell("C1").font = { bold: true, size: 14, name: "Helvetica" };
      worksheet.getCell("C1").alignment = { horizontal: "center", vertical: "middle" };
      worksheet.getCell("G1").value = "DMW Patiala"; // Shifted to G1
      worksheet.getCell("G1").font = { bold: true, size: 12 };
      worksheet.getCell("G1").alignment = { horizontal: "center", vertical: "middle" };
      worksheet.getRow(1).height = 65;
    }
    
    // Footer: "Downloaded by" and "Download Time" (Row 2)
    worksheet.getCell("A2").value = `Downloaded by: ${userName}`;
    worksheet.getCell("A2").font = {
      bold: true,
      size: 10,
      name: "Helvetica",
      italic: true,
    };
    worksheet.getCell("A2").alignment = {
      horizontal: "left",
      vertical: "middle",
    };
    
    // Add "Download Time" on the right (G2)
    worksheet.getCell("G2").value = `Download Time: ${formattedDateTime}`;
    worksheet.getCell("G2").font = {
      bold: true,
      size: 10,
      name: "Helvetica",
      italic: true,
    };
    worksheet.getCell("G2").alignment = {
      horizontal: "right",
      vertical: "middle",
    };
    worksheet.getRow(2).height = 20;
    
    // Add Site and Date Range (Row 3 and 4)
    const siteRow = worksheet.addRow([]);
    siteRow.getCell(1).value = "Site:";
    siteRow.getCell(1).font = { bold: true, size: 11, name: "Calibri" };
    siteRow.getCell(2).value = selectedSiteName || "N/A";
    siteRow.getCell(2).font = { size: 11, name: "Calibri" };
    
    const dateRow = worksheet.addRow([]);
    dateRow.getCell(1).value = "Time Period:";
    dateRow.getCell(1).font = { bold: true, size: 11, name: "Calibri" };
    dateRow.getCell(2).value = `${formattedFromDate} to ${formattedToDate}`;
    dateRow.getCell(2).font = { size: 11, name: "Calibri" };
    
    const generatedRow = worksheet.addRow([]);
    generatedRow.getCell(1).value = "Generated on:";
    generatedRow.getCell(1).font = { bold: true, size: 11, name: "Calibri" };
    generatedRow.getCell(2).value = formattedDateTime;
    generatedRow.getCell(2).font = { size: 11, name: "Calibri" };
    
    worksheet.addRow([]); // Spacer
    
    const overviewRow = worksheet.addRow([]);
    overviewRow.getCell(1).value = "System Logs";
    overviewRow.getCell(1).font = { bold: true, size: 14, name: "Calibri" };
    
    // Table headers
    const headers = [
      "Sr. No.",
      "Log Level",
      "Source Script",
      "Category",
      "Message",
      "Timestamp",
      "Additional Data",
    ];
    
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12, name: "Helvetica" };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "D3D3D3" }, // Light gray background
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
    headerRow.height = 25;
    
    // Add system logs data immediately after header
    if (systemLogs?.length > 0) {
      systemLogs.forEach((log, index) => {
        const rowIndex = 10 + index; // Starting from row 10, immediately after header
        const row = worksheet.addRow([
          index + 1,
          log.LogLevel || "N/A",
          formatSourceScript(log.SourceScript),
          formatCategory(log.Category),
          log.Message || "N/A",
          log.LogTimestamp ? formatDate(log.LogTimestamp) : "N/A",
          formatAdditionalData(log.AdditionalData),
        ]);
        
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          // Left-align Message and Additional Data
          if (colNumber === 5 || colNumber === 6) {
            cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
          } else {
            cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          }
          
          // Highlight log level column based on value
          if (colNumber === 2) { // Log Level column
            const logLevel = cell.value?.toString().toLowerCase();
            if (logLevel === "event") {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "90EE90" }, // Light green background
              };
            } else if (logLevel === "system") {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFE0" }, // Light yellow background
              };
            }
          }
        });
      });
    } else {
      const noDataRow = worksheet.addRow(["No system logs data available"]);
      noDataRow.getCell(1).font = { italic: true, size: 11 };
      noDataRow.getCell(1).alignment = { horizontal: "center" };
      worksheet.mergeCells("A10:F10"); // Merge across 6 columns
    }
    
    // Set column widths based on content
    worksheet.columns = [
      { width: 8 },   // Sr. No.
      { width: 15 },  // Log Level
      { width: 20 },  // Source Script
      { width: 15 },  // Category
      { width: 40 },  // Message
      { width: 20 },  // Timestamp
      { width: 60 },  // Additional Data
    ];
    
    // Generate and download Excel file
    const excelFileName = `System_Logs_${selectedSiteName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    
    // console.log("Generating Excel buffer...");
    const buffer = await workbook.xlsx.writeBuffer();
    // console.log("Excel buffer generated, size:", buffer.byteLength, "bytes");
    
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = excelFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    // console.log("Excel file downloaded successfully");
  } catch (error) {
    console.error("Error generating Excel:", error);
    alert(`Failed to generate Excel: ${error.message}`);
  } finally {
    setIsLoading(false);
  }
};

export default DownloadSystemLogsExcel;